import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useToast } from '../components/Toast';

export interface CannedResponse {
  id: string;
  title: string;
  body: string;
  category?: string | null;
  isActive: boolean;
}

export function useCannedResponses(manage = false) {
  const { toast } = useToast();
  const [responses, setResponses] = useState<CannedResponse[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/canned-responses');
      setResponses(res.data.responses ?? []);
    } catch {
      /* silent — agents without templates just see none */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  const create = async (data: { title: string; body: string; category?: string }) => {
    try {
      await api.post('/admin/canned-responses', data);
      toast('Template saved', 'success');
      await refetch();
      return true;
    } catch (e: any) { toast(e?.response?.data?.message || 'Save failed', 'error'); return false; }
  };

  const update = async (id: string, data: Partial<CannedResponse>) => {
    try {
      await api.put(`/admin/canned-responses/${id}`, data);
      toast('Template updated', 'success');
      await refetch();
      return true;
    } catch (e: any) { toast(e?.response?.data?.message || 'Update failed', 'error'); return false; }
  };

  const remove = async (id: string) => {
    try {
      await api.delete(`/admin/canned-responses/${id}`);
      toast('Template removed', 'success');
      await refetch();
    } catch (e: any) { toast(e?.response?.data?.message || 'Delete failed', 'error'); }
  };

  return { responses, loading, refetch, create, update, remove };
}
